import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { useAuth } from "@/lib/auth.jsx";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { ShieldAlert, CheckCircle2, XCircle, Loader2, Eye, EyeOff } from "lucide-react";
import { toast } from "sonner";

const SPECIAL_RE = /[!@#$%^&*()_+\-=[\]{};:'",.<>/?\\|~`]/g;

function countMatches(str, re) {
  return ((str || "").match(re) || []).length;
}

export default function ChangePassword() {
  const nav = useNavigate();
  const { user, setSession, logout } = useAuth();
  const [policy, setPolicy] = useState({
    min_length: 20, min_spaces: 2, min_digits: 2, min_specials: 2,
    description: "",
  });
  const [pw, setPw] = useState("");
  const [pw2, setPw2] = useState("");
  const [show, setShow] = useState(false);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    api.get("/auth/password-policy").then((r) => setPolicy(r.data)).catch(() => {});
  }, []);

  const checks = useMemo(() => {
    const len = pw.length;
    const spaces = (pw.match(/ /g) || []).length;
    const digits = (pw.match(/\d/g) || []).length;
    const specials = countMatches(pw, SPECIAL_RE);
    return {
      len: { ok: len >= policy.min_length, label: `Mínimo ${policy.min_length} caracteres`, actual: len },
      spaces: { ok: spaces >= policy.min_spaces, label: `Al menos ${policy.min_spaces + 1} palabras (${policy.min_spaces} espacios)`, actual: spaces },
      digits: { ok: digits >= policy.min_digits, label: `Al menos ${policy.min_digits} números`, actual: digits },
      specials: { ok: specials >= policy.min_specials, label: `Al menos ${policy.min_specials} caracteres especiales`, actual: specials },
      match: { ok: pw && pw === pw2, label: "Las dos contraseñas coinciden", actual: null },
    };
  }, [pw, pw2, policy]);

  const allOk = Object.values(checks).every((c) => c.ok);

  const submit = async (e) => {
    e.preventDefault();
    if (!allOk) {
      toast.error("Revisa los requisitos antes de continuar.");
      return;
    }
    setLoading(true);
    try {
      const r = await api.post("/auth/change-password", { new_password: pw });
      setSession(r.data.token, { ...r.data.user, must_change_password: false });
      toast.success("Contraseña actualizada correctamente.");
      nav("/", { replace: true });
    } catch (err) {
      const detail = err?.response?.data?.detail || "Error al cambiar la contraseña";
      toast.error(detail);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#FBFAF6] flex items-center justify-center p-4">
      <div className="w-full max-w-xl bg-white border border-[#E5E2D8] rounded-[20px] p-7 shadow-sm" data-testid="change-password-screen">
        <div className="flex items-start gap-3 mb-5">
          <div className="h-12 w-12 rounded-[14px] bg-[#FCE7DF] flex items-center justify-center shrink-0">
            <ShieldAlert className="h-6 w-6 text-[#D85A30]" />
          </div>
          <div>
            <h1 className="font-heading text-2xl font-bold text-[#2C2C2A]">Refuerzo de seguridad obligatorio</h1>
            <p className="text-sm text-[#7A786F] mt-1">
              Hola {user?.name?.split(" ")[0] || user?.email}. Por motivos de seguridad necesitamos que cambies tu contraseña antes de seguir usando Eksvidi.
            </p>
          </div>
        </div>

        <div className="bg-[#FBFAF6] border border-[#E5E2D8] rounded-[12px] p-3 mb-5">
          <div className="text-xs font-medium text-[#2C2C2A] mb-1">Tu nueva contraseña debe cumplir:</div>
          <div className="text-xs text-[#7A786F] leading-snug">{policy.description}</div>
          <div className="text-[11px] text-[#7A786F] italic mt-2">
            Ejemplo válido: <span className="font-mono text-[#2C2C2A]">Mi gabinete vale más de 2 millones !!</span>
          </div>
        </div>

        <form onSubmit={submit} className="space-y-4" data-testid="change-password-form">
          <div>
            <Label htmlFor="pw">Nueva contraseña</Label>
            <div className="relative">
              <Input
                id="pw"
                type={show ? "text" : "password"}
                value={pw}
                onChange={(e) => setPw(e.target.value)}
                placeholder="Escribe una oración fácil de recordar para ti"
                autoFocus
                autoComplete="new-password"
                className="rounded-[12px] border-[#E5E2D8] pr-10"
                data-testid="new-password-input"
              />
              <button
                type="button"
                onClick={() => setShow((s) => !s)}
                className="absolute right-2 top-1/2 -translate-y-1/2 text-[#7A786F] hover:text-[#2C2C2A] p-1"
                aria-label={show ? "Ocultar" : "Mostrar"}
                tabIndex={-1}
              >
                {show ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
              </button>
            </div>
          </div>

          <div>
            <Label htmlFor="pw2">Repite la contraseña</Label>
            <Input
              id="pw2"
              type={show ? "text" : "password"}
              value={pw2}
              onChange={(e) => setPw2(e.target.value)}
              autoComplete="new-password"
              className="rounded-[12px] border-[#E5E2D8]"
              data-testid="new-password-confirm-input"
            />
          </div>

          <ul className="bg-[#FBFAF6] border border-[#E5E2D8] rounded-[12px] p-3 space-y-1.5" data-testid="policy-checks">
            {Object.entries(checks).map(([key, c]) => (
              <li key={key} className="flex items-center gap-2 text-[13px]">
                {c.ok ? (
                  <CheckCircle2 className="h-4 w-4 text-[#1D9E75] shrink-0" />
                ) : (
                  <XCircle className="h-4 w-4 text-[#B43F3F]/60 shrink-0" />
                )}
                <span className={c.ok ? "text-[#2C2C2A]" : "text-[#7A786F]"}>
                  {c.label}
                  {c.actual !== null && c.actual !== undefined && (
                    <span className="font-mono ml-1 text-[#7A786F]">({c.actual})</span>
                  )}
                </span>
              </li>
            ))}
          </ul>

          <div className="flex items-center justify-between gap-3 pt-1">
            <button
              type="button"
              onClick={() => { logout(); nav("/login", { replace: true }); }}
              className="text-sm text-[#7A786F] hover:text-[#2C2C2A] underline"
              data-testid="change-password-logout-btn"
            >
              Cerrar sesión
            </button>
            <Button
              type="submit"
              disabled={loading || !allOk}
              className="btn-eks-primary rounded-[12px]"
              data-testid="change-password-submit-btn"
            >
              {loading ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : null}
              Guardar y continuar
            </Button>
          </div>
        </form>
      </div>
    </div>
  );
}
